import currency from 'currency.js'

export type CurrencyInput = number | string | currency | undefined | null

// BASE CONVERSION FUNCTIONS...

/**
 * Converts a dollar amount to cents
 *
 * Input: Dollar amount as number, string, currency object, undefined, or null
 * Output: Integer representing cents
 * Example: dollarsToCents(12.34) => 1234
 */
export const dollarsToCents = (value: CurrencyInput) =>
  currency(value || 0).intValue

/**
 * Converts a dollar amount to a currency.js object
 *
 * Input: Dollar amount as number, string, currency object, undefined, or null
 * Output: currency.js object for calculations
 * Example: dollarsToCurrency(12.34) => currency object with value 12.34
 */
export const dollarsToCurrency = (value: CurrencyInput) => currency(value || 0)

/**
 * Converts cents to a currency.js object
 *
 * Input: Cents amount as number, string, currency object, undefined, or null
 * Output: currency.js object for calculations
 * Example: centsToCurrency(1234) => currency object with value 12.34
 */
export const centsToCurrency = (value: CurrencyInput) =>
  currency(value || 0, { fromCents: true })

/**
 * Converts cents to dollar amount as a number
 *
 * Input: Cents amount as number, string, currency object, undefined, or null
 * Output: Dollar amount as number
 * Example: centsToDollars(1234) => 12.34
 */
export const centsToDollars = (value: CurrencyInput) =>
  centsToCurrency(value).value

// FORMATTING FUNCTIONS...

/**
 * Formats a dollar amount to a currency string with commas and dollar sign
 *
 * Input: Dollar amount as number, string, currency object, undefined, or null
 * Output: Formatted currency string with thousands separators
 * Example: formatCurrency(12345.67) => "$12,345.67"
 */
export const formatCurrency = (
  value: CurrencyInput,
  options?: currency.Options & { hideDecimalsIfZero?: boolean }
) => {
  const formatedCur = currency(value || 0, { separator: ',' }).format(options)
  return options?.hideDecimalsIfZero
    ? formatedCur.replace('.00', '')
    : formatedCur
}

/**
 * Converts cents to a formatted currency string with exact precision
 *
 * Input: Cents amount as number, string, currency object, undefined, or null
 * Output: Formatted currency string with exact cents
 * Example: formatCurrencyFromCents(1234) => "$12.34"
 */
export const formatCurrencyFromCents = (
  value: CurrencyInput,
  options?: currency.Options & { hideDecimalsIfZero?: boolean }
) => formatCurrency(centsToDollars(value), options)

/**
 * Converts cents to a formatted currency string, rounding to nearest dollar
 *
 * Input: Cents amount as number, string, currency object, undefined, or null
 * Output: Formatted currency string rounded to nearest dollar
 * Example: formatCurrencyFromCentsAndRound(1267) => "$13.00"
 */
export const formatCurrencyFromCentsAndRound = (
  value: CurrencyInput,
  options?: currency.Options & { hideDecimalsIfZero?: boolean }
) => {
  const roundedDollars = Math.round(centsToDollars(value))
  return formatCurrency(roundedDollars, options)
}

// CALCULATION FUNCTIONS...

/**
 * Adds two amounts in cents and returns the result in cents
 *
 * Input: Two amounts in cents as numbers
 * Output: Sum in cents as integer
 * Example: addCents(1234, 5678) => 6912 (representing $69.12)
 */
export const addCents = (
  firstAmountInCents: number,
  secondAmountInCents: number
) => {
  const firstAmount = centsToCurrency(firstAmountInCents)
  const secondAmount = centsToCurrency(secondAmountInCents)
  return firstAmount.add(secondAmount).intValue
}

/**
 * Sums an array of currency values and returns the total as a number
 *
 * Input: Array of currency amounts as numbers, strings, currency objects, undefined, or null
 * Output: Sum as a number in dollars
 * Example: addCurrencyArray([12.34, 56.78, 90.12]) => 159.24
 */
export const addCurrencyArray = (vals: CurrencyInput[]) =>
  vals.reduce<number>((prev, cur) => currency(prev || 0).add(cur || 0).value, 0)

// TYPE GUARDS...

/**
 * Type guard to check if a value is a currency.js object
 *
 * Input: Any CurrencyInput type
 * Output: Boolean indicating if value is a currency.js object
 * Example: isCurrency(currency(12.34)) => true, isCurrency(12.34) => false
 */
export const isCurrency = (value: CurrencyInput): value is currency => {
  return Boolean(value && typeof value === 'object' && 'value' in value)
}
